import { useState } from "react";
import { Link, Redirect, useLocation } from "wouter";
import { AuthLayout } from "../components/AuthLayout";
import {
  clearMerchantToken,
  hasMerchantCredential,
  merchantFetch,
  setMerchantSession,
  setMerchantToken,
  type MerchantSession,
} from "../lib/merchantAuth";
import type { TenantMeResponse } from "@orbit/api-zod";

/**
 * Merchant sign-in. Two modes (Phase 40):
 *
 *   • Email + password   — for real tenant users (Owner / Manager /
 *                          Staff). POSTs /tenant/auth/login and stores
 *                          the JWT session.
 *   • Tenant API token   — fallback for the cashier device + smoke
 *                          flows. Pasted bearer; verified once with
 *                          GET /tenant/me before persisting.
 */

interface DisambiguationCandidate {
  tenantId: string;
  tenantName: string;
}

export function MerchantLoginPage() {
  const [, navigate] = useLocation();
  const [mode, setMode] = useState<"password" | "token">("password");

  if (hasMerchantCredential()) {
    return <Redirect to="/merchant/dashboard" />;
  }

  return (
    <AuthLayout
      title="Merchant sign in"
      subtitle={
        mode === "password"
          ? "Sign in with your email and password."
          : "Paste your tenant API token (legacy / cashier device)."
      }
    >
      {mode === "password" ? (
        <PasswordLogin
          onSwitch={() => setMode("token")}
          onSuccess={(needsSetPassword) =>
            navigate(needsSetPassword ? "/merchant/account" : "/merchant/dashboard")
          }
        />
      ) : (
        <TokenLogin
          onSwitch={() => setMode("password")}
          onSuccess={() => navigate("/merchant/dashboard")}
        />
      )}
    </AuthLayout>
  );
}

function PasswordLogin({
  onSuccess,
  onSwitch,
}: {
  onSuccess: (needsSetPassword: boolean) => void;
  onSwitch: () => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tenantPick, setTenantPick] = useState<string | undefined>(undefined);
  const [candidates, setCandidates] = useState<DisambiguationCandidate[] | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!email.trim() || !password) {
      setErr("Email and password are required.");
      return;
    }
    setBusy(true);
    try {
      const r = await merchantFetch<
        | MerchantSession
        | { needsTenantSelection: true; candidates: DisambiguationCandidate[] }
      >("/api/v1/tenant/auth/login", {
        method: "POST",
        body: JSON.stringify({
          email: email.trim(),
          password,
          tenantId: tenantPick,
        }),
      });
      if (!r.ok) {
        setErr(r.message || "Sign in failed.");
        return;
      }
      if ("needsTenantSelection" in r.data && r.data.needsTenantSelection) {
        setCandidates(r.data.candidates);
        setErr("This email belongs to multiple tenants — pick one.");
        return;
      }
      const session = r.data as MerchantSession;
      setMerchantSession(session);
      onSuccess(session.user.mustSetPassword);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label htmlFor="m-email" className="label">Email</label>
        <input
          id="m-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="input"
        />
      </div>
      <div>
        <label htmlFor="m-pwd" className="label">Password</label>
        <input
          id="m-pwd"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="input"
        />
      </div>
      {candidates && candidates.length > 0 && (
        <div>
          <label htmlFor="m-tenant" className="label">Tenant</label>
          <select
            id="m-tenant"
            className="input"
            value={tenantPick ?? ""}
            onChange={(e) => setTenantPick(e.target.value || undefined)}
          >
            <option value="">Select a tenant…</option>
            {candidates.map((c) => (
              <option key={c.tenantId} value={c.tenantId}>
                {c.tenantName}
              </option>
            ))}
          </select>
        </div>
      )}
      {err && (
        <p className="rounded-card bg-red-50 px-3 py-2 text-sm text-red-700">
          {err}
        </p>
      )}
      <button type="submit" disabled={busy} className="btn-primary w-full">
        {busy ? "Signing in…" : "Sign in"}
      </button>
      <div className="flex items-center justify-between text-xs">
        <button
          type="button"
          onClick={onSwitch}
          className="text-ink-dark underline-offset-2 hover:text-navy hover:underline"
        >
          Use API token instead
        </button>
        <Link
          href="/merchant/recover"
          className="text-ink-dark underline-offset-2 hover:text-navy hover:underline"
        >
          Forgot token?
        </Link>
      </div>
    </form>
  );
}

function TokenLogin({
  onSuccess,
  onSwitch,
}: {
  onSuccess: () => void;
  onSwitch: () => void;
}) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    const trimmed = token.trim();
    if (!trimmed) {
      setErr("Paste your tenant API token.");
      return;
    }
    setBusy(true);
    try {
      setMerchantToken(trimmed);
      const r = await merchantFetch<TenantMeResponse>("/api/v1/tenant/me");
      if (!r.ok) {
        clearMerchantToken();
        setErr(
          r.status === 401
            ? "That token isn't recognised. Double-check the value from your Console."
            : r.message,
        );
        return;
      }
      onSuccess();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label htmlFor="merchant-token" className="label">
          Tenant API token
        </label>
        <input
          id="merchant-token"
          type="password"
          autoComplete="off"
          spellCheck={false}
          placeholder="orbit_live_…"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          className="input font-mono text-sm"
        />
        <p className="mt-1 text-xs text-ink-dark">
          Stored in this browser only. Sign out from the settings page to
          forget it.
        </p>
      </div>
      {err && (
        <p className="rounded-card bg-red-50 px-3 py-2 text-sm text-red-700">
          {err}
        </p>
      )}
      <button type="submit" disabled={busy} className="btn-primary w-full">
        {busy ? "Verifying…" : "Continue"}
      </button>
      <button
        type="button"
        onClick={onSwitch}
        className="block w-full text-xs text-ink-dark underline-offset-2 hover:text-navy hover:underline"
      >
        Use email and password instead
      </button>
    </form>
  );
}
